import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteUser,
    getUsers,
    updateUserStatus,
} from '../store/users/actions';
import { capitalizeFirst } from '../commonUtils/string.helper';
import { hasPermission } from '../commonUtils/permission.helper';
import constants from '../commonUtils/constants';
import { Switch } from "../components/ui/switch";
import IconButton from '../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

const UsersContext = createContext();

export const useUsers = () => useContext(UsersContext);

export const UsersProvider = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { filters, UsersList, loading, errors } = useSelector((state) => state.Users);
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [updateData, setUpdateData] = useState({ _id: "", title: "" });

    const debouncedGetUsers = useMemo(() => debounce((params) => {
        dispatch(getUsers(params));
    }, 300), [dispatch]);

    const handleToggleStatus = useCallback((id) => dispatch(updateUserStatus(id)), [dispatch]);

    useEffect(() => {
        debouncedGetUsers({
            currentPage: 1,
            perPage: UsersList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters,
        });
    }, [filters, UsersList?.pagination?.perPage, debouncedGetUsers]);

    useEffect(() => {
        handleCloseModal('update');
        handleCloseModal('delete');
    }, [UsersList]);

    const users = useMemo(() => UsersList?.data?.map(
        ({ _id, key, name, email, role, status }, index) => ({
            id: index + 1,
            _id,
            key,
            name,
            email,
            role: role.title,
            status: capitalizeFirst(status),
        })
    ), [UsersList]);

    const pagination = useMemo(() => UsersList?.pagination, [UsersList]);
        
    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetUsers({ currentPage, perPage, ...filters });
    }, [debouncedGetUsers, filters]);

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor('id', { header: '#', cell: info => info.getValue() }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('name', { header: 'Name', cell: info => info.getValue() }),
        columnHelper.accessor('email', { header: 'Email', cell: info => info.getValue() }),
        columnHelper.accessor('role', { header: 'Role', cell: info => info.getValue() }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => (
                <Switch
                    checked={info.getValue() === 'Active'}
                    onCheckedChange={() => handleToggleStatus(info.row.original._id)}
                    disabled={!hasPermission('users.update')}
                />
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    {hasPermission('users.update') && (
                        <IconButton variant="action_default" size="icon" Icon={PencilSquareIcon} onClick={() => navigate(`/users/${info.row.original.key}`)} />
                    )}
                    {hasPermission('users.create_delete') && (
                        <IconButton variant="action_default" size="icon" Icon={TrashIcon} onClick={() => askDeleteConfirmation(info.row.original._id)} />
                    )}
                </>
            ),
        }),
    ], [columnHelper, handleToggleStatus, navigate]);

    const table = useReactTable({
        data: users,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: UsersList?.pagination?.currentPage - 1,
                pageSize: UsersList?.pagination?.perPage,
            },
        },
        manualPagination: true,
        pageCount: UsersList?.pagination?.totalPages,
    });

    const editUser = (data) => setUpdateData(data);
    const handleCloseModal = (action) => {
        if(action === "update"){
            setUpdateData({id:"", title:""})
            setOpenUpdateModal(false);
        } else if(action === "delete") {
            setToDeleteId("");
            setShowDeleteConfirmationDialog(false);
        }
    }
    const askDeleteConfirmation = (id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    };
    const handleDelete = () => {
        dispatch(deleteUser(toDeleteId));
        handleCloseModal('delete');
    };

    return (
        <UsersContext.Provider value={{
            users,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editUser,
            openUpdateModal,
            setOpenUpdateModal,
            updateData,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
        }}>
            {children}
        </UsersContext.Provider>
    );
};
